/**
 * #733 remaining Command Web cues, read from canonical state the room already holds.
 *
 * Nothing here polls, scrapes terminal text, or fills in a phase the records do not carry.
 * A Worker with no recorded phase is left without one.
 */

/** Same 60-minute window machine capacity already uses for recent finished runs. */
export const FINISHED_RECENTLY_MS = 60 * 60_000;

/**
 * Owner words for a live-activity state. States omitted here (UNKNOWN, BLOCKED, HEALTH)
 * are not a work phase, so the card falls through to the Worker status token.
 */
const LIVE_ACTIVITY_PHASE: Readonly<Record<string, string>> = {
  WORKING: 'Working',
  TESTING: 'Testing',
  MERGING: 'Merging',
  DONE: 'Finished',
  REVIEWING: 'Reviewing',
  WAITING: 'Waiting',
};

/** Owner words for a recorded Worker status token. Any other token is not a phase. */
const STATUS_PHASE: Readonly<Record<string, string>> = {
  planning: 'Planning',
  working: 'Working',
  verifying: 'Testing',
  testing: 'Testing',
  merging: 'Merging',
  reviewing: 'Reviewing',
  completed: 'Finished',
  done: 'Finished',
  finished: 'Finished',
  waiting: 'Waiting',
  needs_input: 'Waiting',
  waiting_for_input: 'Waiting for you',
  waiting_for_approval: 'Waiting for you',
  awaiting_approval: 'Waiting for you',
  needs_owner: 'Waiting for you',
};

function phaseFrom(table: Readonly<Record<string, string>>, key: string): string | null {
  if (!Object.prototype.hasOwnProperty.call(table, key)) return null;
  return table[key] ?? null;
}

/**
 * The Worker's current phase, only when a record already says what it is.
 *
 * Owner attention wins, because that wait is the current recorded state. Next is the
 * live-activity state already projected for that Worker. Next is the Worker status token.
 * Anything else is omitted.
 */
export function recordedWorkerPhase(input: {
  readonly status: string;
  readonly attention: { readonly label: string } | null;
  readonly liveState?: string | null;
}): string | null {
  if (input.attention !== null) return 'Waiting for you';
  const live = input.liveState?.trim().toUpperCase() ?? '';
  const fromLive = live === '' ? null : phaseFrom(LIVE_ACTIVITY_PHASE, live);
  if (fromLive !== null) return fromLive;
  return phaseFrom(STATUS_PHASE, input.status.trim().toLowerCase());
}

export function withRecordedWorkerPhases<T extends {
  readonly workerId: string;
  readonly status: string;
  readonly attention: { readonly label: string } | null;
  readonly phase?: string | null;
}>(
  cards: readonly T[],
  rows: readonly { readonly actorKind: string; readonly state: string; readonly refs: { readonly workerId?: string } }[],
): T[] {
  const liveState = new Map<string, string>();
  for (const row of rows) {
    const workerId = row.actorKind === 'worker' ? row.refs.workerId : undefined;
    if (workerId === undefined || liveState.has(workerId)) continue;
    liveState.set(workerId, row.state);
  }
  return cards.map((card) => {
    const phase = recordedWorkerPhase({
      status: card.status,
      attention: card.attention,
      liveState: liveState.get(card.workerId) ?? null,
    });
    return phase === (card.phase ?? null) ? card : { ...card, phase };
  });
}

/** Stable identity of one current Live Activity line. A change is a new arrival. */
export function liveActivityRowSignature(row: {
  readonly actorKey: string;
  readonly state: string;
  readonly action: string;
  readonly since: string | null;
}): string {
  return `${row.actorKey}\u0000${row.state}\u0000${row.action}\u0000${row.since ?? ''}`;
}

/**
 * Actor keys whose current line was not in the previous snapshot.
 * The first snapshot is the baseline: nothing is treated as newly arrived.
 */
export function arrivingLiveActivityKeys(
  previous: readonly { readonly actorKey: string; readonly state: string; readonly action: string; readonly since: string | null }[] | null,
  next: readonly { readonly actorKey: string; readonly state: string; readonly action: string; readonly since: string | null }[],
): ReadonlySet<string> {
  if (previous === null) return new Set();
  const seen = new Set(previous.map(liveActivityRowSignature));
  const arrived = new Set<string>();
  for (const row of next) {
    if (!seen.has(liveActivityRowSignature(row))) arrived.add(row.actorKey);
  }
  return arrived;
}

/** Motion is the highlight. Reduced motion keeps the row and drops the highlight. */
export function shouldHighlightLiveActivityArrival(arrived: boolean, reducedMotion: boolean): boolean {
  return arrived && reducedMotion !== true;
}

/** Clock time for a recorded `since`. Null when the row has no usable time. */
export function liveActivityTimeLabel(since: string | null | undefined): { readonly dateTime: string; readonly label: string } | null {
  if (since == null || since.trim() === '') return null;
  const parsed = Date.parse(since);
  if (Number.isNaN(parsed)) return null;
  return {
    dateTime: new Date(parsed).toISOString(),
    label: new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(parsed),
  };
}

export interface LiveDockCensus {
  readonly working: number;
  readonly idle: number;
  readonly waiting: number;
  readonly finishedRecently: number;
}

/**
 * One count line for the Live dock.
 *
 * Each Worker is counted once, from its activity beacon only:
 * waiting, otherwise active (Working), otherwise idle.
 * Quiet, unknown, and a missing beacon are not placed in those three.
 * Beacon kind `waiting` already includes the recorded waiting and blocked statuses,
 * so blocked Workers count as Waiting rather than as a separate bucket.
 *
 * Finished recently is not a Worker count. It is how many already-loaded finished
 * runs have a `reported_at` inside FINISHED_RECENTLY_MS of now. A missing or future
 * timestamp is skipped. Runs outside the loaded set are not fetched.
 */
export function liveDockCensus(
  cards: readonly { readonly beacon?: { readonly kind: string } | undefined }[],
  finished: readonly { readonly reported_at: string }[],
  nowMs: number,
): LiveDockCensus {
  let working = 0;
  let idle = 0;
  let waiting = 0;
  for (const card of cards) {
    const kind = card.beacon?.kind;
    if (kind === 'waiting') waiting += 1;
    else if (kind === 'active') working += 1;
    else if (kind === 'idle') idle += 1;
  }
  let finishedRecently = 0;
  for (const run of finished) {
    const at = Date.parse(run.reported_at);
    if (Number.isNaN(at)) continue;
    const age = nowMs - at;
    if (age < 0 || age > FINISHED_RECENTLY_MS) continue;
    finishedRecently += 1;
  }
  return { working, idle, waiting, finishedRecently };
}

export function liveDockCensusLabel(census: LiveDockCensus): string {
  return `Working ${census.working} · Idle ${census.idle} · Waiting ${census.waiting} · Finished recently ${census.finishedRecently}`;
}
